import { useState } from "react";
import axios from "axios";
import { Bell, Check } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact "Notify me when PVC is back" email capture form.
 * Two visual variants:
 *   variant="inline"  — slim horizontal form (used on compare-card / small surfaces)
 *   variant="card"    — padded vertical panel with icon & heading (used on PVC configurator)
 */
const PVCNotifyForm = ({ variant = "inline", className = "" }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      setError("Please enter a valid email");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/pvc-waitlist/subscribe`, { email });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't add you to the list right now.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded bg-green-500/10 border border-green-500/30 text-green-400 text-sm ${className}`}
        data-testid="pvc-notify-success"
      >
        <Check className="w-4 h-4 shrink-0" />
        <span>You're on the list — we'll email you when PVC is back.</span>
      </div>
    );
  }

  if (variant === "card") {
    return (
      <div
        className={`bg-gradient-to-b from-blue-500/10 to-[#121212] border border-blue-500/30 rounded-lg p-5 ${className}`}
        data-testid="pvc-notify-card"
      >
        <div className="flex items-center gap-2 mb-2">
          <Bell className="w-5 h-5 text-blue-400" />
          <h3 className="text-lg font-bold text-[#F4F1DE]">Out of stock — want first dibs?</h3>
        </div>
        <p className="text-sm text-[#F4F1DE]/60 mb-4">
          Drop your email and we'll ping you the moment PVC enclosures are back in stock.
        </p>
        <form onSubmit={submit} className="flex gap-2">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            className="bg-[#0A0A0A] border-white/10 text-[#F4F1DE] placeholder-[#F4F1DE]/30 focus:border-blue-400 flex-1"
            disabled={loading}
            data-testid="pvc-notify-email-card"
          />
          <Button
            type="submit"
            disabled={loading}
            className="bg-blue-500 hover:bg-blue-600 text-white font-semibold whitespace-nowrap"
            data-testid="pvc-notify-submit-card"
          >
            {loading ? "Adding..." : "Notify me"}
          </Button>
        </form>
        {error && <p className="text-xs text-red-400 mt-2">{error}</p>}
      </div>
    );
  }

  // inline variant
  return (
    <form onSubmit={submit} className={`space-y-2 ${className}`} data-testid="pvc-notify-inline">
      <div className="flex gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          disabled={loading}
          className="flex-1 bg-[#0A0A0A] border border-white/10 rounded px-3 py-2 text-sm text-[#F4F1DE] placeholder-[#F4F1DE]/30 focus:border-blue-400 focus:outline-none"
          data-testid="pvc-notify-email-inline"
        />
        <button
          type="submit"
          disabled={loading}
          className="px-3 py-2 text-sm bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/40 text-blue-300 font-semibold rounded transition-colors disabled:opacity-40 flex items-center gap-1.5"
          data-testid="pvc-notify-submit-inline"
        >
          <Bell className="w-3.5 h-3.5" />
          {loading ? "..." : "Notify"}
        </button>
      </div>
      {error && <p className="text-xs text-red-400">{error}</p>}
    </form>
  );
};

export default PVCNotifyForm;
